import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { apiHandler } from "../../api";
import { API_BASE_URL, endpoint } from "../../api/endpoint";
import { useDispatch, useSelector } from "react-redux";
import { useToasts } from "react-toast-notifications";
import { formatCurrency } from "../../components/Constants/Constants";
import { ref } from "joi";

export const useSettlementDetails = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { addToast } = useToasts();
  const { authData, storeId } = useSelector((state) => state.loginSlice);
  const { settlementId } = useParams();
  const isMounted = useRef(true);

  // Settlement state
  const [settlementData, setSettlementData] = useState([]);
  const [settlementSummary, setSettlementSummary] = useState(null);
  const [settlementIds, setSettlementIds] = useState({
    settlement_id: null,
    settlement_txn_id: null,
  });

  // Misc settlement state
  const [miscDetails, setMiscDetails] = useState(null);
  const [showMiscModal, setShowMiscModal] = useState(false);

  // UI state
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedOrderId, setSelectedOrderId] = useState(null);
  const [showOrderDetails, setShowOrderDetails] = useState(false);
  const [orderData, setOrderData] = useState([]);
  const [raisingRecon, setRaisingRecon] = useState(false);

  const handleGoBack = () => {
    navigate(-1);
  };

  useEffect(() => {
    const fetchSettlementDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        const result = await apiHandler({
          url: endpoint.GET_SETTLEMENT_DETAILS + settlementId,
          method: "GET",
          authToken: authData,
          dispatch,
          storeId,
        });

        const data = result?.data?.data;
        //console.log("Settlement API result:", data);

        if (isMounted.current && data?.settlements?.length) {
          setSettlementData(data.settlements);
          setSettlementSummary(data.settlement_summary);
          setSelectedOrderId(data.settlements[0].order_id);

          setSettlementIds({
            settlement_id:
              data.settlement_summary?.settlement_id ||
              data.settlements?.[0]?.settlement_id ||
              null,
            settlement_txn_id:
              data.settlement_summary?.settlement_txn_id ||
              data.settlements?.[0]?.settlement_txn_id ||
              null,
          });
        }
      } catch (err) {
        //console.error("Error fetching settlement details:", err);
        if (isMounted.current) setError("Failed to fetch settlement details.");
      } finally {
        setLoading(false);
      }
    };

    if (settlementId) fetchSettlementDetails();

    return () => {
      isMounted.current = false;
    };
  }, [settlementId, authData, dispatch, storeId]);

  /**
   * Fetch order details when selectedOrderId changes
   */
  useEffect(() => {
    if (!selectedOrderId) return;

    const fetchOrderDetails = async () => {
      try {
        const result = await apiHandler({
          url: `${endpoint.GET_ORDER_DETAILS}${selectedOrderId}`,
          method: "GET",
          authToken: authData,
          dispatch,
          storeId,
        });
        setOrderData(result?.data?.data || []);
      } catch (err) {
        //console.error("Error fetching order details:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrderDetails();
  }, [selectedOrderId, authData, dispatch, storeId]);

  /**
   * Toggle order details
   */
  const handleToggleOrderDetails = () => {
    setShowOrderDetails(!showOrderDetails);
  };

  const renderOrderIds = () =>
    settlementData?.map((s) => s.order_id).filter(Boolean);

  const selectedSettlement = settlementData?.find(
    (s) => s.order_id === selectedOrderId
  );

 // Inside useSettlementDetails.js

const handleRaiseRecon = useCallback(
  async (settlementIdOrEvent) => {
    // 1. Prevent event crash
    if (settlementIdOrEvent && typeof settlementIdOrEvent.preventDefault === 'function') {
      settlementIdOrEvent.preventDefault();
      settlementIdOrEvent.stopPropagation();
    }

    // 2. Capture the settlement_id from the argument
    const targetSettlementId = typeof settlementIdOrEvent === 'string' ? settlementIdOrEvent : null;

    if (!selectedSettlement) {
      addToast("No settlement record selected.", { appearance: "error" });
      return;
    }

    try {
      setRaisingRecon(true);

      const payload = {
        order_details: [
          {
            // Use the mongo ID from the selected settlement
            order_id: selectedSettlement.order_object_id, 
            // ENSURE SETTLEMENT_ID IS PASSED HERE
            ...(targetSettlementId && { settlement_id: targetSettlementId }),
          },
        ],
        app_details: {
          buyer_app_id: selectedSettlement.buyer_app_id,
          buyer_app_uri: selectedSettlement.buyer_app_uri,
          seller_app_id: selectedSettlement.seller_app_id,
          seller_app_uri: selectedSettlement.seller_app_uri,
        },
      };

      const response = await apiHandler({
        url: `${endpoint.RAISE_RECON}`,
        method: "POST",
        data: JSON.stringify(payload),
        headers: {
          "Content-Type": "application/json",
          "tenantid": storeId, 
        },
        authToken: authData,
        dispatch,
      });

      const status = response?.status || response?.data?.status;
      if (status === 200 || status === 201) {
        addToast("Recon raised successfully", { appearance: "success", autoDismiss: true });
      } else {
        throw new Error(response?.data?.message || "Failed to raise recon");
      }
    } catch (err) {
      addToast(err.message || "Error occurred.", { appearance: "error" });
    } finally {
      setRaisingRecon(false);
    }
  },
  [selectedSettlement, authData, storeId, dispatch, addToast]
);
  const handleLatestSettlementRequest = useCallback(
    async (e) => {
      e.preventDefault();

      try {
        setLoading(true);

        const _id = selectedSettlement?._id;
        if (!_id) {
          addToast("Order ID not found!", {
            appearance: "error",
            autoDismiss: true,
          });
          return;
        }

        const url = `${endpoint.RAISE_REPORT}/${_id}`;
        const response = await apiHandler({
          url,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          authToken: authData,
          dispatch,
          storeId,
        });

        const responseStatus = response?.data?.status || response?.status;
        const toastMessage =
          response?.data?.response?.data?.message ||
          "Settlement requested successfully";

        addToast(toastMessage, {
          appearance: responseStatus === 200 ? "success" : "error",
          autoDismiss: true,
        });
      } catch (error) {
        //console.error("API Error:", error);
        addToast(
          error.response?.data?.message || "Unexpected error occurred.",
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
      } finally {
        setLoading(false);
      }
    },
    [authData, dispatch, storeId, selectedSettlement, addToast]
  );
const handleOpenModal = async () => {
    try {
        setLoading(true);

        const res = await apiHandler({
            url:
                endpoint.GET_SETTLEMENT_DETAILS + settlementId,
            method: "GET",
            authToken: authData,
            dispatch,
            storeId,
        });

        //console.log("Misc Settlement Response:", res?.data?.data);

        const apiData = res?.data?.data;

        const settlements = apiData?.settlements;

        if (!settlements || settlements.length === 0) {
            throw new Error("No settlement records found in the API response.");
        }

        const specificSettlement = settlements.find(
            (s) => s.order_id === selectedOrderId
        ) || settlements[0];

        if (!specificSettlement) {
            throw new Error("Could not find the specific settlement record.");
        }

        setMiscDetails({
            order_id: specificSettlement.order_id,
            self: {
                amount: specificSettlement.self_amount,
                status: specificSettlement.self_status,
                reference_no: specificSettlement.self_reference_no,
            },
            provider: {
                bank_details: specificSettlement.provider?.bank_details,
                provider_amount: specificSettlement.provider_amount,
                provider_id: specificSettlement.provider?.provider_id,
                provider_name: specificSettlement.provider?.provider_name,
                status: specificSettlement.provider_status,
                reference_no: specificSettlement.provider_reference_no,
            },
        });

        setShowMiscModal(true);
    } catch (err) {
        //console.error("Error fetching misc settlement details:", err);
        addToast("Failed to fetch settlement details.", {
            appearance: "error",
            autoDismiss: true,
        });
    } finally {
        setLoading(false);
    }
};
  const handleMiscSettlementRequest = useCallback(
    async (details) => {
      if (!details) return;

      try {
        setLoading(true);

        const selfAmount = details?.self?.amount;
        const providerAmount = String(details?.provider?.provider_amount ?? "");

        const requestBody = {
          settle_details: [
            {
              self: { amount: selfAmount },
              provider: {
                bank_details: {
                  account_no: details?.provider?.bank_details?.account_no,
                  ifsc_code: details?.provider?.bank_details?.ifsc_code,
                },
                id: details?.provider?.provider_id,
                name: details?.provider?.provider_name,

                amount: providerAmount,
              },
              orderId: selectedSettlement?.order_id,
            },
          ],
          transaction_id: settlementIds?.settlement_txn_id, 
        };


        const url = `${endpoint.SEND_MISC_SETTLEMENT_REQUEST}`;
        const response = await apiHandler({
          url,
          method: "POST",
          headers: { "Content-Type": "application/json" },
          authToken: authData,
          dispatch,
          data: requestBody,
          storeId,
        });

        const responseStatus = response?.data?.status || response?.status;
        const toastMessage =
          response?.data?.message || "Misc Settlement requested successfully";

        addToast(toastMessage, {
          appearance: responseStatus === 200 ? "success" : "error",
          autoDismiss: true,
        });
      } catch (error) {
        //console.error("Misc Settlement API Error:", error);
        addToast(
          error.response?.data?.message || "Unexpected error occurred.",
          {
            appearance: "error",
            autoDismiss: true,
          }
        );
      } finally {
        setLoading(false);
      }
    },
    [authData, dispatch, storeId, settlementIds, addToast]
  );

  const handleCloseModal = () => setShowMiscModal(false);

  const handleConfirmAction = async () => {
    try {
      await handleMiscSettlementRequest(miscDetails);
      handleCloseModal();
    } catch (err) {
      //console.error("Misc Settlement Request failed:", err);
    }
  };

  return {
    settlementId,
    settlementData,
    settlementSummary,
    settlementIds,
    error,
    loading,
    orderData,
    showOrderDetails,
    selectedOrderId,
    selectedSettlement,
    renderOrderIds,
    handleToggleOrderDetails,
    setSelectedOrderId,
    handleLatestSettlementRequest,
    // Misc settlement
    miscDetails,
    showMiscModal,
    handleOpenModal,
    handleCloseModal,
    handleConfirmAction,
    handleGoBack,
    handleRaiseRecon
  };
};
